// shelf-row — slot-layout primitive for the shelf family (book-row, wall-shelf).
//
// 🔴 ASK-139 F-139af — THE MANIFEST LANE, LAID OUT BY THE ONE PLACEMENT ENGINE.
//
// This primitive was drafted for the 2026-05-28 shelf cutover, un-registered by that day's pivot,
// and the legacy builders it stood beside were retired by DECISION-052 — so from then on a shelf had
// no renderer at all. MEASURED on the deployed plane (ASK-139 §64, lost-boys-comics): a hardcover
// seated on `BOOK_ROW_ANCHOR_INST_CF0038B4` slot 1, the row's subtree exactly one node (its own body).
//
// It no longer carries its own arithmetic. The draft packed from a hard-coded dimension table (one
// "book" 20 mm wide, whatever the SKU) — the per-SKU problem D-062 already solved. The catalogue
// socket says HOW the row seats (`distribution: run`, `pose: edge-out` | `face-out`, `accepts`), the
// manifest lane says WHERE (`slots[0].volume.w` across the board, at the socket's height and depth),
// and the grammar does the rest exactly as it does for the longbox: per-SKU dimensions from the
// registry, each item spending its own packing span, `domain full` named rather than overflowed.
//
// Why the lane comes from the MANIFEST and not the socket: the shelf family's sockets are S2
// annotations (`grammarSource: "derived"`, `domain: point`) — a point has no length, and running a
// row off a point marches it straight off the end of the board (measured: item 17 of 40 at x 0.51 on
// a 1 m shelf). The lane volume is the authored length of the same row; when Content authors the
// socket as a spline (the longbox's LANE), the composer's grammar path takes it and this file retires.

import * as THREE from "../../../../vendor/three.module.js";
import { registerSlotLayout, getSlotLayout } from "./index.js";

const DEFAULT_LANE_W = 1.0;
const END_MARGIN = { "edge-out": 0.01, "face-out": 0.05 };   // a row of spines runs to the bracket; face-out stock breathes

function computeShelfRow(ctx) {
	const { anchor, manifest, slotDef, laneSocket, catalogueFrame } = ctx;
	const def = slotDef || (Array.isArray(manifest?.slots) ? manifest.slots[0] : null) || {};
	const grammar = getSlotLayout("grammar");
	if (!grammar) return null;

	// The row's own words. A catalogue that has no lane socket still has a manifest orientation.
	const pose = laneSocket?.pose || (def.orientation === "face-out" ? "face-out" : "edge-out");
	const accepts = laneSocket?.accepts?.length ? laneSocket.accepts : null;
	const origin = laneSocket?.position || { x: 0, y: 0, z: 0 };
	const laneW = Number.isFinite(def.volume?.w) ? def.volume.w : Math.max(0.2, anchor?.scale?.x ?? DEFAULT_LANE_W);
	const half = Math.max(0, laneW / 2 - (END_MARGIN[pose] ?? 0.01));

	// Left → right as the customer stands at the shelf (catalogue-local −X → +X; the shelf's front
	// is local +Z). Slot 1 is therefore the left-hand end, which is where a reader starts.
	const socket = {
		id: laneSocket?.id ?? null,
		domain: "spline",
		distribution: "run",
		pose,
		accepts,
		fit: laneSocket?.fit ?? null,
		capacity: laneSocket?.capacity,
		capacitySource: laneSocket?.capacitySource,
		grammarSource: "manifest-lane",
		geometry: { points: [{ x: origin.x - half, y: origin.y, z: origin.z }, { x: origin.x + half, y: origin.y, z: origin.z }] },
	};
	// The approach is the SHELF's front (+Z in its own frame), turned with the body — not the
	// composer's box-family `frontDir`, which a shelf manifest never declares.
	const yaw = Number.isFinite(catalogueFrame?.yaw) ? catalogueFrame.yaw : (anchor?.rotationY || 0);
	const approach = new THREE.Vector3(0, 0, 1).applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw);

	const out = grammar({ ...ctx, socket, frontDir: approach });
	if (!out) return null;
	// Said on the scene truth, so an instrument can tell this lane from an AUTHORED grammar socket.
	out.extras = { ...out.extras, approach, shelfPose: pose, grammar: out.extras?.grammar ? { ...out.extras.grammar, source: "manifest-lane" } : null };
	return out;
}

registerSlotLayout("shelf-row", computeShelfRow);
export { computeShelfRow };
